"use client";

import { createClient } from "@/lib/supabase/client";
import { sessionStore } from "@/lib/auth/session-store";
import { impersonationStore } from "@/lib/impersonation/impersonation-store";

const getApiUrl = () => {
  const url = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";
  if (!url.startsWith("http")) {
    return `https://${url}`;
  }
  return url;
};

const API_URL = getApiUrl();

interface RequestOptions extends RequestInit {
  requireAuth?: boolean;
  _isRetry?: boolean; // Internal flag to prevent infinite retries
}

export class ApiError extends Error {
  readonly status: number;
  readonly requestId?: string;

  constructor(message: string, status: number, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.requestId = requestId;
  }
}

class ApiClient {
  private getAuthHeaders(): Record<string, string> {
    // Get session from in-memory store (fast, synchronous)
    const token = sessionStore.getAccessToken();

    if (!token) {
      return {};
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    // Add impersonation header if impersonating
    const impersonatedUserId = impersonationStore.getUserId();
    if (impersonatedUserId) {
      headers["X-Impersonate-User"] = impersonatedUserId;
    }

    return headers;
  }

  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { requireAuth = true, headers = {}, _isRetry = false, ...restOptions } = options;

    const authHeaders = requireAuth ? this.getAuthHeaders() : {};

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...restOptions,
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...headers,
      },
    });

    // Handle 401 Unauthorized - attempt token refresh once
    if (response.status === 401 && requireAuth && !_isRetry) {
      try {
        const supabase = createClient();
        const { data: { session }, error } = await supabase.auth.refreshSession();

        if (!error && session) {
          // Retry request with new token
          return this.request<T>(endpoint, { ...options, _isRetry: true });
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
      }
    }

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        message?: string | string[];
        requestId?: string;
      };
      const message = Array.isArray(body.message)
        ? body.message.join(", ")
        : body.message || `Request failed: ${response.status}`;
      const requestId =
        body.requestId ?? response.headers.get("x-request-id") ?? undefined;
      throw new ApiError(message, response.status, requestId);
    }

    // Handle empty responses (204 No Content, empty DELETE responses, etc.)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    // Check if response has content before parsing JSON
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('Failed to parse JSON response:', text);
      throw new Error('Invalid JSON response from server');
    }
  }

  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  // Notifications API
  notifications = {
    list: (params?: { unreadOnly?: boolean; limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.offset) searchParams.set('offset', params.offset.toString());

      const query = searchParams.toString();
      return this.get<import('@otalytics/shared').ListNotificationsResponse>(
        `/api/notifications${query ? `?${query}` : ''}`
      );
    },

    markAsRead: (notificationId: string) => {
      return this.patch<{ success: boolean }>(
        `/api/notifications/${notificationId}/read`
      );
    },

    markAllAsRead: () => {
      return this.patch<{ success: boolean; count: number }>(
        `/api/notifications/read-all`
      );
    },

    dismiss: (notificationId: string) => {
      return this.patch<{ success: boolean }>(
        `/api/notifications/${notificationId}/dismiss`
      );
    },

    undismiss: (notificationId: string) => {
      return this.patch<{ success: boolean }>(
        `/api/notifications/${notificationId}/undismiss`
      );
    },

    listResolved: (params?: { limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.offset) searchParams.set('offset', params.offset.toString());

      const query = searchParams.toString();
      return this.get<import('@otalytics/shared').Notification[]>(
        `/api/notifications/resolved${query ? `?${query}` : ''}`
      );
    },
  };
}

export const api = new ApiClient();
